import type {
  ProcedureCode,
  ProcedureVisitInput,
  UpdateProcedureCode,
} from "@dentacore/shared-types";

import { describeNumberError } from "../ui/number-field";
import type { NumberFieldRules } from "../ui/number-field";

/**
 * The rules behind the Add and Edit dialog (TR4, TR5) — no React, so they can be
 * tested on their own. They were spread across `settings/catalogue-panel.tsx`,
 * `procedure-edit.tsx` and `procedure-visits.tsx` while the list was a table.
 */

/** The most visits one procedure may have: the API's bound and the table's CHECK (PV1). */
export const MAX_VISITS = 20;

export const GAP: NumberFieldRules = { min: 1, max: 365, integer: true, unit: "days" };
export const MINUTES: NumberFieldRules = { min: 5, max: 480, integer: true, unit: "minutes" };
export const PRICE: NumberFieldRules = { min: 0 };

/** One visit as the dialog holds it — strings, because they are what was typed. */
export interface VisitDraft {
  title: string;
  gap: string;
  minutes: string;
  price: string;
}

/** A new procedure: one visit, nothing typed yet. */
export function emptyDrafts(): VisitDraft[] {
  return [{ title: "", gap: "", minutes: "", price: "" }];
}

/**
 * A stored procedure as drafts: its visits, or — for a one-visit procedure,
 * which has no visit rows — one draft holding the row's own length and price.
 */
export function draftsFromCode(code: ProcedureCode): VisitDraft[] {
  if (code.visits.length === 0) {
    return [
      {
        title: "",
        gap: "",
        minutes: String(code.defaultDurationMinutes),
        price: minorToMajor(code.defaultPriceCents),
      },
    ];
  }
  return code.visits.map((visit) => ({
    title: visit.title ?? "",
    gap: visit.gapDays === null ? "" : String(visit.gapDays),
    minutes: String(visit.durationMinutes),
    price: minorToMajor(visit.priceCents),
  }));
}

/**
 * **The typed visit count, or null when it is not one** (TR5): a whole number
 * from 1 to 20. Anything else is refused whole — never clamped, because a
 * clamped 25 would quietly become 20 visits somebody did not ask for.
 */
export function parseVisitCount(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const count = Number(trimmed);
  return count >= 1 && count <= MAX_VISITS ? count : null;
}

/**
 * Grow from the end, each new visit as long as the last; shrink from the end.
 *
 * **Called once the count is committed, never per keystroke** — typing 12
 * passes through 1, and resizing on that 1 would throw away visits 2 to 11.
 */
export function resizeDrafts(drafts: readonly VisitDraft[], count: number): VisitDraft[] {
  if (count <= drafts.length) return drafts.slice(0, count);
  const minutes = drafts.at(-1)?.minutes ?? "";
  return [
    ...drafts,
    ...Array.from({ length: count - drafts.length }, () => ({
      title: "",
      gap: "",
      minutes,
      price: "",
    })),
  ];
}

/** Every visit has a length and a price in range; a gap, where given, too. */
export function visitsValid(drafts: readonly VisitDraft[]): boolean {
  return drafts.every(
    (row, index) =>
      row.minutes.trim() !== "" &&
      row.price.trim() !== "" &&
      describeNumberError(row.minutes, MINUTES) === null &&
      describeNumberError(row.price, PRICE) === null &&
      (index === 0 || describeNumberError(row.gap, GAP) === null),
  );
}

/** Drafts as the server takes them (PV1) — the first visit carries no gap. */
export function toVisitInputs(drafts: readonly VisitDraft[]): ProcedureVisitInput[] {
  return drafts.map((row, index) => ({
    ...(row.title.trim() === "" ? {} : { title: row.title.trim() }),
    ...(index === 0 || row.gap.trim() === "" ? {} : { gapDays: Number(row.gap) }),
    durationMinutes: Number(row.minutes),
    priceCents: majorToMinor(row.price),
  }));
}

/** The whole procedure's price: what its visits add up to (PV2). */
export function totalOf(drafts: readonly VisitDraft[]): number {
  return drafts.reduce(
    (sum, row) => sum + (row.price.trim() === "" ? 0 : majorToMinor(row.price)),
    0,
  );
}

/**
 * What an edit changes (STORY-151), or null when it changes nothing.
 *
 * **What is sent is what changed.** The row's own fields go as a `PATCH` of only
 * the fields that differ, so an edit to the name cannot overwrite a price
 * somebody else changed a moment ago. The visits go as the whole list, and only
 * when the list differs: `[]` to make it one visit again, two or more to set
 * them, `null` to leave them alone. A one-visit procedure's minutes and price
 * are the row's own and travel in the `PATCH`.
 */
export function editChanges(
  code: ProcedureCode,
  codeText: string,
  label: string,
  drafts: readonly VisitDraft[],
  categoryId: string = code.procedureCategoryId,
): { body: UpdateProcedureCode; visits: ProcedureVisitInput[] | null } | null {
  const body: UpdateProcedureCode = {};
  if (codeText.trim() !== code.code) body.code = codeText.trim();
  if (label.trim() !== code.label) body.label = label.trim();
  // Its category (PG-D4) — sent only when it moved, like every other field.
  if (categoryId !== code.procedureCategoryId) body.procedureCategoryId = categoryId;

  let visits: ProcedureVisitInput[] | null = null;
  const only = drafts.length === 1 ? drafts[0] : undefined;
  if (only !== undefined) {
    const minutes = Number(only.minutes);
    const cents = majorToMinor(only.price);
    if (code.visits.length > 0) {
      visits = [];
      body.defaultDurationMinutes = minutes;
      body.defaultPriceCents = cents;
    } else {
      if (minutes !== code.defaultDurationMinutes) body.defaultDurationMinutes = minutes;
      if (cents !== code.defaultPriceCents) body.defaultPriceCents = cents;
    }
  } else {
    const next = toVisitInputs(drafts);
    if (JSON.stringify(next) !== JSON.stringify(toVisitInputs(draftsFromCode(code)))) {
      visits = next;
    }
  }

  return Object.keys(body).length === 0 && visits === null ? null : { body, visits };
}

/** How many days a course runs, first visit to last — the gaps that are set. */
export function spanDays(code: ProcedureCode): number {
  return code.visits.reduce(
    (sum, visit, index) => sum + (index === 0 ? 0 : (visit.gapDays ?? 0)),
    0,
  );
}

export function plural(count: number, word: string): string {
  return `${String(count)} ${count === 1 ? word : `${word}s`}`;
}

/**
 * `"120.50"` → `12050`, without a float.
 *
 * `120.10 * 100` is 12009.999… in binary floating point. Money is built from
 * the digits or not at all — the same rule the server's importer follows.
 */
export function majorToMinor(value: string): number {
  const [whole, fraction = ""] = value.trim().split(".");
  const minor = (fraction + "00").slice(0, 2);
  return Number(whole || 0) * 100 + Number(minor);
}

/**
 * The exact inverse of `majorToMinor`, for filling the form from a stored row
 * (Anthony's QA, item 11 — Duplicate).
 *
 * **Paired with `majorToMinor` deliberately, and not with `formatMinor`.** That
 * one is for reading and respects the currency's own exponent; this one has to
 * round-trip — whatever it puts in the box must come back out as the same
 * number of cents when the form is saved. Sharing the hundred with the function
 * it reverses is what guarantees that, and if the input path ever learns about
 * currency exponents both of these change together.
 */
export function minorToMajor(minor: number): string {
  return (minor / 100).toFixed(2);
}

/**
 * Minor units back to something a human reads.
 *
 * **The exponent comes from the currency, not from a hardcoded two.** `Intl`
 * knows that JPY has none and KWD has three, and a clinic billing in either
 * would otherwise see every price off by a factor of a hundred or a thousand.
 */
export function formatMinor(minor: number, currency: string): string {
  try {
    const format = new Intl.NumberFormat(undefined, { style: "currency", currency });
    // `maximumFractionDigits` is optional in the type even though a currency
    // format always resolves one; two is the fallback, and reaching it means
    // the runtime told us nothing rather than that the currency has two.
    const digits = format.resolvedOptions().maximumFractionDigits ?? 2;
    return format.format(minor / 10 ** digits);
  } catch {
    // An unknown currency code must not blank the row — fall back to the raw
    // amount with the code beside it.
    return `${(minor / 100).toFixed(2)} ${currency}`;
  }
}
